package com.arenaproplay.admin.ui.components.admin

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.AppEffects
import com.arenaproplay.admin.ui.theme.displaySmall
import kotlinx.browser.document
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.*

/**
 * Court Light right-side drawer panel.
 *
 * - Fixed 420 px wide panel anchored to the right edge of the viewport.
 * - Semi-transparent scrim covers the left — click to close.
 * - Pressing Escape also fires [onClose].
 * - 200 ms slide-in animation ([open] becomes true) and 200 ms slide-out
 *   animation ([open] becomes false). The panel stays mounted for the full
 *   slide-out duration before being removed from the DOM, so the exit
 *   animation plays completely. Esc/scrim behaviour is unchanged.
 */
@Composable
fun Drawer(
    open: Boolean,
    onClose: () -> Unit,
    title: String,
    content: @Composable () -> Unit
) {
    // `visible` tracks whether the DOM panel should remain mounted.
    // It becomes true immediately when open=true, and stays true for
    // 200 ms after open=false so the slide-out animation finishes.
    var visible by remember { mutableStateOf(open) }
    // `closing` drives the slide-out CSS animation class.
    var closing by remember { mutableStateOf(false) }

    LaunchedEffect(open) {
        if (open) {
            closing = false
            visible = true
        } else {
            closing = true
            delay(200)
            visible = false
            closing = false
        }
    }

    // Keyboard listener — Esc closes
    DisposableEffect(open) {
        val handler: (dynamic) -> Unit = { event ->
            if (open && (event.key as? String) == "Escape") onClose()
        }
        document.addEventListener("keydown", handler)
        onDispose { document.removeEventListener("keydown", handler) }
    }

    if (!visible) return

    val drawerAnim = if (closing)
        "arena-drawer-out 200ms cubic-bezier(0.16, 1, 0.3, 1) both"
    else
        "arena-drawer-in 200ms cubic-bezier(0.16, 1, 0.3, 1) both"
    val scrimAnim = if (closing)
        "arena-scrim-out 200ms ease both"
    else
        "arena-scrim-in 200ms ease both"

    // Full-screen scrim
    Box(
        Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(500)
            .styleModifier {
                property("background", "rgba(0, 0, 0, 0.55)")
                property("backdrop-filter", "blur(2px)")
                property("-webkit-backdrop-filter", "blur(2px)")
                property("animation", scrimAnim)
            }
            .onClick { onClose() }
    ) {
        // Drawer panel — right side, stop click propagation
        Box(
            Modifier
                .position(Position.Absolute)
                .top(0.px).right(0.px).bottom(0.px)
                .width(420.px)
                .classNames("na-drawer-panel")
                .styleModifier {
                    property("background", "#0B1220")
                    property("box-shadow", AppEffects.ModalElevation)
                    property("animation", drawerAnim)
                    property("overflow-y", "auto")
                    property("-webkit-overflow-scrolling", "touch")
                }
                .onClick { it.stopPropagation() }
        ) {
            Column(Modifier.fillMaxWidth().fillMaxHeight()) {
                // Header
                Row(
                    Modifier
                        .fillMaxWidth()
                        .padding(leftRight = 24.px, topBottom = 20.px)
                        .styleModifier { property("border-bottom", "1px solid #1C2634") },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        title,
                        Modifier.weight(1f).displaySmall().color(AppColors.Ink)
                    )
                    // Close button
                    Box(
                        Modifier
                            .width(32.px).height(32.px)
                            .borderRadius(50.percent)
                            .cursor(Cursor.Pointer)
                            .styleModifier {
                                property("background", "#101828")
                                property("display", "flex")
                                property("align-items", "center")
                                property("justify-content", "center")
                                property("transition", "background 140ms ease")
                            }
                            .onClick { onClose() },
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            "×",
                            Modifier.fontSize(18.px).color(AppColors.Muted)
                                .styleModifier { property("line-height", "1") }
                        )
                    }
                }

                // Content area
                Box(Modifier.fillMaxWidth().weight(1f).padding(24.px)) {
                    content()
                }
            }
        }
    }
}
